/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { type Registry as Services, service } from '@ember/service';
import { type Filter } from 'uplisting-frontend/utils/filters/types';
import { FilterId } from 'uplisting-frontend/utils/filters/abstract';
import FilterMonth from 'uplisting-frontend/components/ui/filters/filter/month';
import FilterSingleSelect from 'uplisting-frontend/components/ui/filters/filter/single-select';
import FilterMultiSelect from 'uplisting-frontend/components/ui/filters/filter/multi-select';
import FilterMultiSelectList from 'uplisting-frontend/components/ui/filters/filter/multi-select-list';
import { FilterComponent } from 'uplisting-frontend/utils/filters/abstract/-base';

interface IArgs {
  filter: Filter;
  hideLabel?: boolean;
  onFilterUpdate(filter: Filter): void;
}

interface FiltersFilterSignature {
  Element: HTMLDivElement;

  Args: IArgs;
}

export default class UiFiltersFilterComponent extends Component<FiltersFilterSignature> {
  @service intl!: Services['intl'];

  @cached
  get filter(): Filter {
    return this.args.filter;
  }

  @cached
  get filterComponent():
    | typeof FilterMonth
    | typeof FilterSingleSelect
    | typeof FilterMultiSelect
    | typeof FilterMultiSelectList {
    switch (this.filter.component) {
      case FilterComponent.multiSelect:
        return FilterMultiSelect;
      case FilterComponent.singleSelect:
        return FilterSingleSelect;
      case FilterComponent.month:
        return FilterMonth;
      case FilterComponent.multiSelectList:
        return FilterMultiSelectList;
    }
  }

  @cached
  get filterKey(): string {
    switch (this.filter.id) {
      case FilterId.clientName:
        return 'client_name';
      case FilterId.property:
        return 'property';
      case FilterId.airbnbQualityState:
        return 'airbnb_quality_state';
      case FilterId.airbnbOfficialLink:
        return 'airbnb_link';
      case FilterId.airbnbOfficialConnection:
        return 'airbnb_connection';
      case FilterId.clientStatementStatus:
        return 'client_statement_status';
      case FilterId.clientStatementPaymentStatus:
        return 'client_statement_payment_status';
      case FilterId.period:
        return 'period';
      case FilterId.upsellOrderStatus:
        return 'upsell_order_status';
      case FilterId.airbnbCancellationPolicy:
        return 'airbnb_cancellation_policy';
      case FilterId.country:
        return 'country';
      case FilterId.city:
        return 'city';
      case FilterId.knowledgeCenterCategory:
        return 'knowledge_center_category';
      case FilterId.knowledgeCenterStatus:
        return 'knowledge_center_status';
      case FilterId.protectionStatus:
        return 'protection_status';
      case FilterId.bookingWindow:
        return 'booking_window';
      case FilterId.disputeStatus:
        return 'dispute_status';
    }
  }

  @cached
  get label(): string {
    return this.intl.t(`filters.${this.filterKey}.label`);
  }

  @cached
  get labelClass(): string {
    return this.args.hideLabel ? 'visually-hidden' : '';
  }

  @cached
  get placeholder(): string {
    return this.intl.t(`filters.${this.filterKey}.placeholder`);
  }

  @cached
  get noMatchesMessage(): string {
    return this.intl.t(`filters.${this.filterKey}.no_data`);
  }
}
